import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface Review {
  user: {
    _id: string;
    username: string;
    profilePhoto: {
      fileKey: string;
      fileType: string;
      featured: boolean;
      url: string;
    };
  };
  rating: number;
  review: string;
  ratedAt: string;
}

interface ReviewResponse {
  totalReviews: number;
  averageRating: number;
  reviews: Review[];
  currentUserRating: number;
  currentUserReview: string;
}

interface PlacesState {
  places: any[];
  currentPage: number;
  hasMore: boolean;
  loading: boolean;
}

const initialState: PlacesState = {
  places: [],
  currentPage: 1,
  hasMore: true,
  loading: false,
};

const PlacesSlice = createSlice({
  name: 'places',
  initialState,
  reducers: {
    setPlaces: (state, action: PayloadAction<any[]>) => {
      state.places = action.payload;
      state.currentPage = 1;
      state.hasMore = action.payload.length > 0;
    },
    appendPlaces: (state, action: PayloadAction<any[]>) => {
      const existingIds = new Set(state.places.map(place => place._id));
      const uniqueNewPlaces = action.payload.filter(place => !existingIds.has(place._id));
      state.places = [...state.places, ...uniqueNewPlaces];
      state.currentPage += 1;
      state.hasMore = action.payload.length > 0;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setHasMore: (state, action: PayloadAction<boolean>) => {
      state.hasMore = action.payload;
    },
    resetPlaces: (state) => {
      state.places = [];
      state.currentPage = 1;
      state.hasMore = true;
      state.loading = false;
    },
    updatePlace: (state, action: PayloadAction<any>) => {
      const index = state.places.findIndex(place => place._id === action.payload._id);
      if (index !== -1) {
        // Merge existing data with the incoming update to preserve any unchanged fields
        const updatedPlace = { ...state.places[index], ...action.payload };
        state.places[index] = updatedPlace;
      } else {
        // If the place doesn't exist in the array yet, add it (useful for detail fetches without list fetch first)
        state.places.push(action.payload);
      }
      
    },
    updatePlaceReview: (state, action: PayloadAction<{ _id: string; reviewData: ReviewResponse }>) => {
      const index = state.places.findIndex(place => place._id === action.payload._id);
      if (index !== -1) {
        const { reviewData } = action.payload;
        
        // Update the place with review information
        state.places[index].totalReviews = reviewData.totalReviews;
        state.places[index].averageRating = reviewData.averageRating;
        state.places[index].reviews = reviewData.reviews;
        state.places[index].currentUserRating = reviewData.currentUserRating;
        state.places[index].currentUserReview = reviewData.currentUserReview;
        
        // Update topReviews if reviews exist
        if (reviewData.reviews && reviewData.reviews.length > 0) {
          state.places[index].topReviews = reviewData.reviews;
        }
      }
    }
  },
});

export const { setPlaces, appendPlaces, setLoading, setHasMore, resetPlaces, updatePlace, updatePlaceReview } = PlacesSlice.actions;
export default PlacesSlice.reducer;
